import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { SmsClass, SmsLinkType, SmsLinkTypes } from '@app/core/models/message';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActionService } from '@app/cx-builder/message-builder/services/action.service';
import { SmsService } from '@app/core/services/sms.service';
import { CustomValidators } from '@app/core/utils/custom-validators';
import { FeatureService } from '@app/core/services/feature.service';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { CharacterLimits } from '@app/core/utils/characterLimits';
import { FormInputStateControlComponent } from '../form-input-state-control/form-input-state-control.component';
import { MentionModule } from 'angular-mentions';
import { ErrorDisplayComponent } from '../error-display/error-display.component';
import { NgClass } from '@angular/common';
import { RadioBtnComponent } from '../radio-btn/radio-btn.component';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'rn-sms-builder',
  templateUrl: './sms-builder.component.html',
  styleUrls: ['./sms-builder.component.scss'],
  standalone: true,
  imports: [
    FormInputStateControlComponent,
    FormsModule,
    ReactiveFormsModule,
    MentionModule,
    ErrorDisplayComponent,
    NgClass,
    RadioBtnComponent,
    MatSelectModule,
    MatFormFieldModule,
  ],
})
export class SmsBuilderComponent implements OnInit {
  @ViewChild('smsBodyTextarea') smsBodyTextarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('smsDisclaimerInput') smsDisclaimerInput: ElementRef<HTMLInputElement>;
  // captured on (blur): choosing from the field select steals focus before the insert lands
  private smsBodySelection: { start: number; end: number } | null = null;
  private smsDisclaimerSelection: { start: number; end: number } | null = null;
  // set by insertDynamicField, consumed by restorePendingFocus() on the select's (closed) event
  private pendingFocusRestore: { element: HTMLTextAreaElement | HTMLInputElement; position: number } | null = null;
  // underlying mat-select value; always reset to undefined after a pick so the
  // next selection — even of the same option — registers as a value change and
  // fires (selectionChange). Trigger always shows dynamicInputsPlaceholder instead.
  bodyFieldSelectValue: string | undefined;
  disclaimerFieldSelectValue: string | undefined;
  @Input() smsModel: SmsClass;
  @Input() smsFormGroup: UntypedFormGroup;
  @Input() isTwoWaySms: boolean = false;
  @Input() customInputs: string[] = [];
  @Input() maxCharacterCount: number = CharacterLimits.SmsTextCharLimit;
  @Input() contactCardEnabled: boolean = false;
  @Input() bodyPlaceholder: string = '';
  @Input() disclaimerPlaceholder: string = '';
  @Input() alternateTextPlaceholder: string = '';
  @Input() showDestination: boolean = true;
  @Input() isNotificationPing: boolean = false;
  fullLinkTypes: SmsLinkTypes;
  linkTypes: SmsLinkType[];
  selectedLinkType: UntypedFormControl;
  standardInputs: string[] = [
    'account_ccid',
    'account_firstname',
    'account_lastname',
    'account_product_group',
    'account_secondary_account_id',
  ];
  alternateInputs: string[] = [];
  dynamicInputs = [];
  dynamicInputsPlaceholder: string = '@{account_}';
  mentionConfig = {
    mentionSelect: this.formatter,
  };

  _wireMessageIsShown: boolean = true;

  constructor(
    private actionService: ActionService,
    private smsService: SmsService,
    private featureService: FeatureService,
    public newFeed: NewFeedTransitionService,
  ) {
    this.fullLinkTypes = smsService.allLinkTypes;
  }

  get wireMessageIsShown(): boolean {
    return this._wireMessageIsShown;
  }

  @Input() set wireMessageIsShown(value: boolean) {
    this._wireMessageIsShown = value;
    this.linkTypes = this.getLinkTypesList();
    const linkValues = this.linkTypes.map((l) => l.value);
    if (this.selectedLinkType !== undefined) {
      const linkIsValid = linkValues.includes(this.selectedLinkType.value);

      // only change the sms destination if the current destination is incompatible
      // with the current wire message configuration
      if (!linkIsValid) {
        // apparently angular doesn't like when the selectedLinkType is changed
        // synchronously with wire.is_shown, so this timeout splits these changes
        // into two events. (I think it has something to do with the experience
        // preview)
        setTimeout(() => {
          this.selectedLinkType.setValue(this.fullLinkTypes.none.value);
        }, 5);
      }
    }
  }

  ngOnInit() {
    this.linkTypes = this.getLinkTypesList();
    this.selectedLinkType = new UntypedFormControl();
    if (this.isTwoWaySms) {
      this.selectedLinkType.disable();
    }

    this.getLinkType();
    this.toggleAlternateSMS();
    this.setDynamicInputs();
    this.setAlternateInputs();

    this.selectedLinkType.valueChanges.subscribe(() => {
      this.processLinkType();
    });

    this.smsFormGroup.controls['sms_body'].valueChanges.subscribe(() => {
      this.smsModel.body = this.smsFormGroup.get('sms_body').value;
    });

    this.smsFormGroup.controls['alternate_sms_text'].valueChanges.subscribe(
      () => {
        this.smsModel.alternate_text =
          this.smsFormGroup.get('alternate_sms_text').value;
      },
    );

    this.smsFormGroup.controls['sms_disclaimer'].valueChanges.subscribe(() => {
      this.smsModel.disclaimer = this.smsFormGroup.get('sms_disclaimer').value;
    });

    this.smsFormGroup.controls['auth_link'].valueChanges.subscribe(() => {
      this.smsModel.auth_link = this.smsFormGroup.get('auth_link').value;
      this.smsService.updateAuthLink(this.smsFormGroup.get('auth_link').value);
    });
  }

  getLinkTypesList(): SmsLinkType[] {
    const filteredFFOptions = Object.keys(this.fullLinkTypes)
      .map((key) => this.fullLinkTypes[key])
      .filter((linkType) => {
        if (linkType.feature_flag.length === 0) {
          return true;
        } else {
          return this.featureService.checkFlag(linkType.feature_flag);
        }
      });
    const filteredFFOptionsAndWireFeedReqs = filteredFFOptions.filter(
      (linkType) => {
        if (linkType.requires_wire_only) {
          return !this.wireMessageIsShown;
        } else if (this.wireMessageIsShown) {
          return true;
        } else {
          return !linkType.requires_feed_message;
        }
      },
    );

    // Filter out contact card if vCardEnabled is false
    const filteredWithVCardCheck = filteredFFOptionsAndWireFeedReqs.filter(
      (linkType) => {
        if (linkType.value === this.fullLinkTypes.contact_card.value) {
          return this.contactCardEnabled;
        }
        return true;
      },
    );

    return filteredWithVCardCheck;
  }

  setDynamicInputs(): void {
    this.dynamicInputs = this.standardInputs.concat(this.customInputs);
    this.bodyFieldSelectValue = undefined;
    this.disclaimerFieldSelectValue = undefined;
  }

  setAlternateInputs(): void {
    this.alternateInputs = this.isNotificationPing
      ? ['auth-link']
      : ['auth-link', 'contact-card'];
  }

  captureBodySelection(event: FocusEvent): void {
    this.smsBodySelection = this.captureSelection(event.target as HTMLTextAreaElement);
  }

  captureDisclaimerSelection(event: FocusEvent): void {
    this.smsDisclaimerSelection = this.captureSelection(event.target as HTMLInputElement);
  }

  private captureSelection(
    element: HTMLTextAreaElement | HTMLInputElement,
  ): { start: number; end: number } {
    return {
      start: element.selectionStart ?? element.value.length,
      end: element.selectionEnd ?? element.value.length,
    };
  }

  onBodyFieldSelected(field: string): void {
    this.selectDynamicField(
      field,
      (f) => this.insertIntoBody(f),
      (value) => (this.bodyFieldSelectValue = value),
    );
  }

  onDisclaimerFieldSelected(field: string): void {
    this.selectDynamicField(
      field,
      (f) => this.insertIntoDisclaimer(f),
      (value) => (this.disclaimerFieldSelectValue = value),
    );
  }

  private selectDynamicField(
    field: string,
    insert: (field: string) => void,
    resetSelectValue: (value: string | undefined) => void,
  ): void {
    insert(field);
    // reset to undefined (not dynamicInputs[0]) so picking the same field again is still
    // a value change for mat-select — otherwise (selectionChange) won't fire on a no-op select
    resetSelectValue(undefined);
  }

  insertIntoBody(field: string): void {
    this.smsBodySelection = this.insertDynamicField(
      field,
      this.smsFormGroup.controls['sms_body'] as UntypedFormControl,
      this.smsBodyTextarea?.nativeElement,
      this.smsBodySelection,
    );
  }

  insertIntoDisclaimer(field: string): void {
    this.smsDisclaimerSelection = this.insertDynamicField(
      field,
      this.smsFormGroup.controls['sms_disclaimer'] as UntypedFormControl,
      this.smsDisclaimerInput?.nativeElement,
      this.smsDisclaimerSelection,
    );
  }

  // `selection` must be captured on (blur), not read live here — focus has already moved by the time this runs
  private insertDynamicField(
    field: string,
    control: UntypedFormControl,
    element: HTMLTextAreaElement | HTMLInputElement | undefined,
    selection: { start: number; end: number } | null,
  ): { start: number; end: number } {
    const token = this.formatter({ label: field });
    const currentValue: string = control.value ?? '';
    const start = Math.min(selection?.start ?? currentValue.length, currentValue.length);
    const end = Math.min(selection?.end ?? start, currentValue.length);

    const nextValue = currentValue.slice(0, start) + token + currentValue.slice(end);
    control.setValue(nextValue);

    const nextCursorPosition = start + token.length;
    this.pendingFocusRestore = element
      ? { element, position: nextCursorPosition }
      : null;

    return { start: nextCursorPosition, end: nextCursorPosition };
  }

  // called from (closed) on the field-select mat-selects — deferring to that
  // event (rather than setTimeout) avoids racing mat-select's own focus handling
  // when its panel closes
  restorePendingFocus(): void {
    if (!this.pendingFocusRestore) {
      return;
    }
    const { element, position } = this.pendingFocusRestore;
    element.focus();
    element.setSelectionRange(position, position);
    this.pendingFocusRestore = null;
  }


  getLinkType(): void {
    switch (this.smsModel.auth_link) {
      case this.fullLinkTypes.details.auth_link:
        this.selectedLinkType.setValue(this.fullLinkTypes.details.value);
        return;
      case this.fullLinkTypes.feed_experience.auth_link:
        this.selectedLinkType.setValue(
          this.fullLinkTypes.feed_experience.value,
        );
        return;
      case this.fullLinkTypes.top_of_feed.auth_link:
        this.selectedLinkType.setValue(this.fullLinkTypes.top_of_feed.value);
        return;
      case this.fullLinkTypes.url.auth_link:
        this.selectedLinkType.setValue(this.fullLinkTypes.url.value);
        return;
      case this.fullLinkTypes.custom_tracked_url.auth_link:
        this.selectedLinkType.setValue(
          this.fullLinkTypes.custom_tracked_url.value,
        );
        return;
      case this.fullLinkTypes.none.auth_link:
        this.selectedLinkType.setValue(this.fullLinkTypes.none.value);
        return;
      case this.fullLinkTypes.contact_card.auth_link:
        this.selectedLinkType.setValue(this.fullLinkTypes.contact_card.value);
        return;
      default:
        this.selectedLinkType.setValue(this.fullLinkTypes.url.value);
    }
    this.smsFormGroup.controls['auth_link'].setValidators([
      Validators.required,
      CustomValidators.validateReverseReservedInputURL,
    ]);
  }

  processLinkType(): void {
    switch (this.selectedLinkType.value) {
      case this.fullLinkTypes.details.value:
        this.smsModel.auth_link = this.fullLinkTypes.details.auth_link;
        break;
      case this.fullLinkTypes.feed_experience.value:
        this.smsModel.auth_link = this.fullLinkTypes.feed_experience.auth_link;
        break;
      case this.fullLinkTypes.top_of_feed.value:
        this.smsModel.auth_link = this.fullLinkTypes.top_of_feed.auth_link;
        break;
      case this.fullLinkTypes.url.value:
        this.smsModel.auth_link = this.fullLinkTypes.url.auth_link;
        break;
      case this.fullLinkTypes.custom_tracked_url.value:
        this.smsModel.auth_link =
          this.fullLinkTypes.custom_tracked_url.auth_link;
        break;
      case this.fullLinkTypes.none.value:
        this.smsModel.auth_link = this.fullLinkTypes.none.auth_link;
        break;
      case this.fullLinkTypes.contact_card.value:
        this.smsModel.auth_link = this.fullLinkTypes.contact_card.auth_link;
        break;
      default:
        this.smsModel.auth_link = this.fullLinkTypes.url.auth_link;
    }

    this.smsFormGroup.controls['auth_link'].setValue(this.smsModel.auth_link);

    // remove validators for the "none" link type
    if (this.selectedLinkType.value === this.fullLinkTypes.none.value) {
      this.smsFormGroup.controls['auth_link'].clearValidators();
    } else {
      this.smsFormGroup.controls['auth_link'].setValidators([
        Validators.required,
        CustomValidators.validateReverseReservedInputURL,
      ]);
    }
  }

  toggleAlternateSMS(): void {
    if (!this.smsModel.is_alternate_message) {
      this.smsFormGroup.controls['alternate_sms_text'].disable();
    } else {
      this.smsFormGroup.controls['alternate_sms_text'].enable();
    }
  }
  //04-09-2025: Character counter no longer needs 160 limit.
  smsCharacterCount(): number {
    return this.smsService.remainingCharacterCount(
      this.smsModel.body,
      this.smsModel.disclaimer,
      !!this.wireMessageIsShown,
    );
  }

  alternateSMSCharacterCount(): number {
    return this.smsService.remainingAlternateCharacterCount(
      this.smsModel.alternate_text,
    );
  }

  formatter(mentionInfo: { label: string }): string {
    return `@{${mentionInfo.label}} `;
  }

  /**
   * Shows the readonly controls if true, shows the editable if false.
   */
  showReadOnlyIntro() {
    let showReadOnly: boolean;

    switch (this.selectedLinkType.value) {
      case this.fullLinkTypes.url.value:
        showReadOnly = false;
        break;
      default:
        showReadOnly = true;
        break;
    }

    return showReadOnly;
  }





  showSmsBodyError() {
    return (
      !this.smsFormGroup.controls['sms_body'].valid &&
      this.smsFormGroup.controls['sms_body'].touched
    );
  }

  getTooltipData(errors: Object): string | void {
    return this.actionService.getTooltipData(errors);
  }

  authLinkHasError(): boolean {
    return (
      this.selectedLinkType.value !== this.fullLinkTypes.none &&
      !this.smsFormGroup.controls['auth_link'].valid &&
      this.smsFormGroup.controls['auth_link'].touched
    );
  }

  showErrorToolTip(): boolean {
    return (
      !!this.smsFormGroup.controls['sms_disclaimer'] &&
      !this.smsFormGroup.controls['sms_disclaimer'].valid &&
      this.smsFormGroup.controls['sms_disclaimer'].touched
    );
  }

  showAlternateInputError(): boolean {
    return (
      !this.smsFormGroup.controls['alternate_sms_text'].valid &&
      this.smsFormGroup.controls['alternate_sms_text'].touched
    );
  }
}
